import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  WEB_COMPONENT_GZ_BUTTON,
  WEB_COMPONENT_GZ_LABELED_CONTENT,
  WEB_COMPONENT_GZ_SIDE_MENU,
  WEB_COMPONENT_GZ_TEXT,
} from '../../../webcomponents.constants';
import {
  Error,
  Integration,
  IntegrationEvents,
  IntegrationStatus,
  NSVACloudPlatform,
  Sensor,
  SensorEnabledStatus,
  SensorResponseStatus,
  SensorStatus,
} from '../../common/types';
import { GzLabeledContentWebComponent } from '../../../ui/gzLabeledContent';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../ui/gzButton/types';
import { IntegrationDetailsPanelEvents } from '../types';
import { GzDialogWebComponent, GzTooltipWebComponent } from '../../../index';
import { SensorType } from '../../sensors/types/sensor-type';
import { INTEGRATION_WITHOUT_EDIT, IntegrationType, SENSORS_BY_INTEGRATION_MAP } from '../../types/integration-type';
import { AwsSensorView } from '../../sensors/aws/view/AwsSensorView';
import { CreateIntegrationEvent } from '../../addIntegrationPanel/types/CreateIntegrationEvent';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { NsvaNetworkSensorView } from '../../sensors/nsva/view/NsvaNetworkSensorView';
import { SensorConnectivityErrors } from '../../sensors/constants';
import { AzureCWSensorView } from '../../sensors/azureCW/view/AzureCWSensorView';
import { GoogleWorkspaceSensorView } from '../../sensors/googleWorkspace/view/GoogleWorkspaceSensorView';
import { ADOnPremSensorView } from '../../sensors/ad-onprem/view/ADOnPremSensorView';
import { GCPSensorView } from '../../sensors/gcp/view/GCPSensorView';
import { AtlassianJiraConfluenceView } from '../../sensors/atlassian/atlassianJiraConfluence/view/AtlassianJiraConfluenceView';
import { AtlassianAuditView } from '../../sensors/atlassian/atlassianAudit/view/AtlassianAuditView';

// O365 Integration is not included here because the audit sensor is added in the db if it's not configured
const integrationWithNotConfiguredSensors = [IntegrationType.ATLASSIAN_CLOUD];

const sensorStatusesToBullets = {
  [SensorStatus.ACTIVE]: 'bullet-success',
  [SensorStatus.OFFLINE]: 'bullet-danger',
  [SensorStatus.DISABLED]: 'bullet-warning',
  [SensorStatus.NOT_CONFIGURED]: 'bullet-neutral',
  [SensorStatus.ACTION_NEEDED]: 'bullet-danger',
  [SensorStatus.BAD_AUDIT_POLICY_CONFIG]: 'bullet-neutral',
  [SensorStatus.BAD_CERTIFICATE_AUDIT_POLICY_CONFIG]: 'bullet-neutral',
  [SensorStatus.BEST_NOT_INSTALLED]: 'bullet-neutral',
  [SensorStatus.RESTART_NEEDED]: 'bullet-neutral',
  [SensorStatus.NOT_LICENSED]: 'bullet-danger',
  [SensorStatus.UNKNOWN]: 'bullet-neutral',
};

const sensorResponseStatusToBullets = {
  [SensorResponseStatus.ACTIVE]: 'bullet-success',
  [SensorResponseStatus.ERROR]: 'bullet-danger',
  [SensorResponseStatus.UNKNOWN]: 'bullet-neutral',
  [SensorResponseStatus.NOT_AVAILABLE]: 'bullet-danger',
  [SensorResponseStatus.DISABLED]: 'bullet-warning',
};

const notShowedSensorConnectivityErrors = [
  SensorConnectivityErrors.RESTART_REQUIRED,
  SensorConnectivityErrors.DISABLED,
];

const sensorsWithEnabledStatusAction = [
  SensorType.O365_MAIL,
  SensorType.O365_AUDIT,
  SensorType.ATLASSIAN_CONFLUENCE,
  SensorType.ATLASSIAN_JIRA,
];
const sensorsWithDeleteAction = [SensorType.AD_ONPREM_DOMAIN];

const unknownSensorStatusBullet = 'bullet-neutral';

export default class IntegrationDetailsPanelView extends BaseWebComponentView {
  private hasIntegrationFeature = false;
  private numberOfSensorsDisplayedPerType: Record<number, number> = {};

  getTranslations() {
    this.translations = this.stringsTranslator.getSensorsIntegrationsStrings();
  }

  setTranslatedStrings() {
    this.dom.$description.label = this.translations.descriptionLabel;
    this.dom.$details.label = this.translations.detailsLabel;
    this.dom.$company.label = this.translations.companyLabel;
  }

  getDomElements() {
    this.dom.$sideMenu = this.shadow.querySelector(WEB_COMPONENT_GZ_SIDE_MENU);
    this.dom.$name = this.shadow.querySelector('#integration-name');
    this.dom.$description = this.shadow.querySelector('#integration-description');
    this.dom.$details = this.shadow.querySelector('#integration-details');
    this.dom.$company = this.shadow.querySelector('#integration-company');
    this.dom.$integrationDetailsButton = this.shadow.querySelector('#integration-details-button');
    this.dom.$sensorsContainer = this.shadow.querySelector('#integration-sensors');
    this.dom.$actionsContainer = this.shadow.querySelector('#integration-actions');
    this.dom.$sensorsExtraInfoContainer = this.shadow.querySelector('#integration-sensor-extra-info');
    this.dom.$commonData = this.shadow.querySelector('#integration-common-data');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(data: Integration) {
    this.hasIntegrationFeature = data.hasIntegrationFeature;
    this.numberOfSensorsDisplayedPerType = {};

    this.dom.$name.innerText = data.name;
    this.dom.$description.innerText =
      typeof this.translations.integrationDescription[data.type] !== 'undefined'
        ? this.translations.integrationDescription[data.type]
        : '';
    if (!this.dependencies.Ext.userHasRight('companyManager')) {
      toggleVisibility(this.dom.$details, false);
    }
    this.dom.$integrationDetailsButton.innerText = this.dependencies.translateFn('View full license details');
    this.dom.$company.innerText = data.companyName;

    this.renderCommonData(data.sensors, data.type);
    this.renderSensors(data.sensors, data.type);
    this.renderActions(data.status, data.type, data.sensors, data.companyId, data.nonUserConfigurable);
  }

  openSensorSetDisabledStatusDialog(sensor: Sensor): void {
    this.buildSetDisabledStatusDialog(
      this.translations.sensorDisableDialogTitle,
      this.translations.sensorDisableDialogText,
      () =>
        document.body.dispatchEvent(
          new CustomEvent(IntegrationEvents.SENSOR_SET_ENABLED_STATUS, {
            detail: {
              sensor,
              enabledStatus: SensorEnabledStatus.DISABLED,
            },
          })
        )
    );
  }

  openSetDisabledStatusDialog(integration: Integration): void {
    let warningMessage = '';
    if (integration.type === IntegrationType.AWS) {
      warningMessage = AwsSensorView.getPopupWarningMessage(
        this.dependencies,
        this.dependencies.translateFn('Disabling the sensor will not deactivate the following paid services: ')
      );
    }
    this.buildSetDisabledStatusDialog(
      this.dependencies.translateFn('Disable the {integrationName} sensor integration?', {
        integrationName: integration.name,
      }),
      this.translations.disableDialogText[integration.type] + warningMessage,
      () =>
        document.body.dispatchEvent(
          new CustomEvent(IntegrationEvents.INTEGRATION_SET_ENABLED_STATUS, {
            detail: {
              integration,
              enabledStatus: SensorEnabledStatus.DISABLED,
            },
          })
        )
    );
  }

  private getWarningMessageForDeletion(integrationType: number): string {
    let warningMessage = this.translations.deleteDialogTextWarning[integrationType];
    if (warningMessage) {
      warningMessage = `<div class="warning-sensor-delete mt-2">
      <span class="ds-icon ds-icon-notification_warning warning mr-2" style="float: left; margin-right: 5px; font-size: 20px"></span>
      <p style="font-weight: 470">${warningMessage}</p>
      </div>`;
    } else {
      warningMessage = '';
    }

    if (integrationType === IntegrationType.AWS) {
      warningMessage = AwsSensorView.getPopupWarningMessage(
        this.dependencies,
        this.dependencies.translateFn('Deleting the sensor will not deactivate the following paid services: ')
      );
    }
    return warningMessage;
  }

  openDeleteDialog(integration: Integration): void {
    const deleteDialogText = this.translations.deleteDialogText[integration.type];
    const warningMessage = this.getWarningMessageForDeletion(integration.type);

    const windowBodyHtml = `
      <div style="margin-top: -18px">
        <${WEB_COMPONENT_GZ_TEXT} class="mb-2" fontSize="${FontSizes.HEADING_6}">
          ${deleteDialogText}
          ${warningMessage}
        </${WEB_COMPONENT_GZ_TEXT}>
      </div>
    `;

    const windowFooterHtml = `
      <${WEB_COMPONENT_GZ_BUTTON}
        id="cancel"
        class="mr-2"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.SECONDARY}"
      >
        ${this.translations.deleteDialogCancelButtonLabel}
      </${WEB_COMPONENT_GZ_BUTTON}>
      <${WEB_COMPONENT_GZ_BUTTON} id="confirm" type="${ButtonTypes.OUTLINE}" color="${ButtonColors.NEGATIVE}">
        ${this.translations.deleteDialogConfirmButtonLabel}
      </${WEB_COMPONENT_GZ_BUTTON}>
    `;

    const confirmCallback = () =>
      document.body.dispatchEvent(new CustomEvent(IntegrationEvents.INTEGRATION_DELETE, { detail: integration }));

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      this.dependencies.translateFn('Delete the {integrationName} sensor integration?', {
        integrationName: integration.name,
      }),
      null,
      confirmCallback
    );
  }

  onDeleteSensorConfirmationWindow(sensor: Sensor): void {
    let windowBodyHtml = ``;
    let windowTitle = ``;

    switch (sensor.type) {
      case SensorType.AD_ONPREM_DOMAIN:
        windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn(
          'The offline domain controller sensor will no longer process data and it will be removed from the Details Panel.'
        )}
      </p>
      <p>
        ${this.dependencies.translateFn(
          'If the domain controller sensor comes back online, it will be automatically added to the integration and it will resume processing data.'
        )}
      </p>`;
        windowTitle = this.dependencies.translateFn('Delete the {sensorName} domain controller sensor?', {
          sensorName: sensor.name,
        });
        break;
      default:
        break;
    }

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Delete')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      () => document.body.dispatchEvent(new CustomEvent(IntegrationEvents.DELETE_SENSOR, { detail: sensor })),
      null,
      true
    );
  }

  private renderCommonData(sensors: Sensor[], integrationType: IntegrationType): void {
    switch (integrationType) {
      case IntegrationType.NSVA:
        this.dom.$commonData.appendChild(NsvaNetworkSensorView.buildCommonData(sensors[0], this.dependencies));
        break;
      default:
        break;
    }
  }

  private renderSensors(sensors: Sensor[], integrationType: IntegrationType): void {
    const sensorTypeCountMap = sensors.reduce((acc: Record<number, number>, sensor: Sensor) => {
      if (acc[sensor.type]) {
        acc[sensor.type]++;
      } else {
        acc[sensor.type] = 1;
      }
      return acc;
    }, {});

    if (integrationWithNotConfiguredSensors.includes(integrationType)) {
      for (const sensorType of SENSORS_BY_INTEGRATION_MAP[integrationType]) {
        if (!sensors.find(sensor => sensor.type === sensorType)) {
          sensors.push({
            _id: '',
            lastError: null,
            lastErrorStatus: 0,
            name: null,
            specifics: {},
            type: sensorType,
            status: SensorStatus.NOT_CONFIGURED,
            responseActionStatus: SensorResponseStatus.UNKNOWN,
          });
        }
      }
    }

    sensors.sort((first, second) => {
      if (first.status === SensorStatus.OFFLINE && second.status !== SensorStatus.OFFLINE) {
        return -1;
      }
      if (first.status !== SensorStatus.OFFLINE && second.status === SensorStatus.OFFLINE) {
        return 1;
      }
      if (first.status === SensorStatus.NOT_CONFIGURED && second.status !== SensorStatus.NOT_CONFIGURED) {
        return -1;
      }
      if (first.status !== SensorStatus.NOT_CONFIGURED && second.status === SensorStatus.NOT_CONFIGURED) {
        return 1;
      }
      return 0;
    });

    sensors.forEach((sensor: Sensor) => {
      if (sensor.type === SensorType.NSVA &&
        sensor.specifics.cloud &&
        sensor.specifics.cloud.data &&
        Object.values(NSVACloudPlatform).includes(sensor.specifics.cloud.platform as NSVACloudPlatform)
      ) {
        this.dom.$sensorsContainer.appendChild(
          NsvaNetworkSensorView.buildSensorCloud(
            sensor.specifics.cloud.platform,
            sensor.specifics.cloud.data,
            this.dependencies
          )
        );
      }
      this.dom.$sensorsContainer.appendChild(this.buildSensorElement(sensor, sensors.length === 1, sensorTypeCountMap));
      if (sensor.type === SensorType.NSVA) {
        NsvaNetworkSensorView.buildVirtualApplianceInfo(sensor, this.dom.$sensorsExtraInfoContainer, this.dependencies);
        const warningMessage = this.buildSensorWarningMessageHtml(sensor);
        const warningMessageElement = document.createElement('div');
        warningMessageElement.innerHTML = warningMessage;
        this.dom.$sensorsExtraInfoContainer.appendChild(warningMessageElement);
      }
    });
  }

  private buildSensorElement(
    sensor: Sensor,
    isJustOneSensor: boolean,
    sensorTypeCountMap: Record<number, number>
  ): GzLabeledContentWebComponent {
    const element = new GzLabeledContentWebComponent(),
      sensorLabel = this.computeSensorLabel(sensor, sensorTypeCountMap),
      sensorTypeHtml = sensor.type === SensorType.NSVA ? '' : this.buildSensorTypeHtml(sensor),
      sensorSpecifics = this.buildSensorSpecificsHtml(sensor),
      statusHtml = this.buildSensorStatusHtml(sensor),
      responseStatusHtml = this.buildSensorResponseStatusHtml(sensor),
      statusErrorHtml = sensor.lastErrorStatus ? this.buildSensorErrorHtml(sensor.lastError, sensor.lastErrorStatus) : '',
      warningMessage = this.buildSensorWarningMessageHtml(sensor),
      deleteSensorButton = sensorsWithDeleteAction.includes(sensor.type)
                           ? this.buildDeleteSensorButton(sensor, isJustOneSensor)
                           : '',
      setEnabledStatusActionButton = sensorsWithEnabledStatusAction.includes(sensor.type)
                                     ? this.buildSensorActionButton(sensor)
                                     : '',
      extraInfo = this.buildSensorExtraInfo(sensor);


    element.className = 'integration-sensor';
    element.label = sensorLabel;
    element.labelFontWeight = FontWeights.MEDIUM;
    element.stacked = true;

    element.innerHTML = `
      ${sensorTypeHtml}
      ${sensorSpecifics}
      ${statusHtml}
      ${statusErrorHtml}
      ${responseStatusHtml}
    `;

    if (sensor.responseActionErrorMessage &&
        (sensor.responseActionStatus === SensorResponseStatus.ERROR ||
         sensor.responseActionStatus === SensorResponseStatus.NOT_AVAILABLE)) {
      const responseReasonElement = this.createSensorReasonElement(sensor.responseActionErrorMessage);
      element.appendChild(responseReasonElement);
    }

    if (extraInfo) {
      element.appendChild(extraInfo);
    }

    if (sensor.type !== SensorType.NSVA) {
      const warningMessageElement = document.createElement('div');
      warningMessageElement.innerHTML = warningMessage;
      element.appendChild(warningMessageElement);
    }

    if (setEnabledStatusActionButton) {
      element.appendChild(setEnabledStatusActionButton);
    }

    if (deleteSensorButton) {
      element.appendChild(deleteSensorButton);
    }

    return element;
  }

  private computeSensorTypeLabel(sensor: Sensor): string {
    return typeof this.translations.sensorNames[sensor.type] !== 'undefined'
      ? this.translations.sensorNames[sensor.type]
      : this.translations.unknownSensorTypeLabel;
  }

  private computeLabelForMultipleSensors(sensor: Sensor, sensorTypeCountMap: Record<number, number>): string {
    if (!sensorTypeCountMap[sensor.type] || sensorTypeCountMap[sensor.type] === 1) {
      return typeof this.translations.sensorNames[sensor.type] !== 'undefined'
        ? this.translations.sensorNames[sensor.type]
        : this.translations.unknownSensorTypeLabel;
    }

    if (this.numberOfSensorsDisplayedPerType[sensor.type]) {
      this.numberOfSensorsDisplayedPerType[sensor.type]++;
    } else {
      this.numberOfSensorsDisplayedPerType[sensor.type] = 1;
    }

    return typeof this.translations.sensorNames[sensor.type] !== 'undefined'
      ? this.translations.sensorNames[sensor.type] + ' ' + this.numberOfSensorsDisplayedPerType[sensor.type]
      : this.translations.unknownSensorTypeLabel;
  }

  private computeSensorLabel(sensor: Sensor, sensorTypeCountMap: Record<number, number>): string {
    let label = '';
    switch (sensor.type) {
      case SensorType.O365_AUDIT:
      case SensorType.O365_MAIL:
      case SensorType.AWS:
      case SensorType.AZURE_AD_AUDIT:
      case SensorType.INTUNE:
      case SensorType.GOOGLE_WS:
      case SensorType.ATLASSIAN_AUDIT:
      case SensorType.AZURE_AD_EVENT_HUB:
        label = this.computeSensorTypeLabel(sensor);
        break;
      case SensorType.ATLASSIAN_JIRA:
      case SensorType.ATLASSIAN_CONFLUENCE:
        label = this.computeLabelForMultipleSensors(sensor, sensorTypeCountMap);
        break;
      case SensorType.NSVA:
        label = this.dependencies.translateFn('Network Sensor module');
        break;
      case SensorType.AD_ONPREM_DOMAIN:
        label = sensor.name as string;
        break;
      case SensorType.AZURE_CW:
        label = this.dependencies.translateFn('Subscription');
        break;
      default:
        break;
    }

    return label;
  }

  private buildSensorTypeHtml(sensor: Sensor): string {
    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${this.translations.sensorTypeLabel}">
                ${this.computeSensorTypeLabel(sensor)}
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  private buildSensorSpecificsHtml(sensor: Sensor): string {
    let specifics = '';
    switch (sensor.type) {
      case SensorType.NSVA:
        specifics = NsvaNetworkSensorView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.AZURE_CW:
        specifics = AzureCWSensorView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.GOOGLE_WS:
        specifics = GoogleWorkspaceSensorView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.AD_ONPREM_DOMAIN:
        specifics = ADOnPremSensorView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.GCP:
        specifics = GCPSensorView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.ATLASSIAN_JIRA:
      case SensorType.ATLASSIAN_CONFLUENCE:
        specifics = AtlassianJiraConfluenceView.getSensorSpecifics(sensor, this.dependencies);
        break;
      case SensorType.ATLASSIAN_AUDIT:
        specifics = AtlassianAuditView.getSensorSpecifics(sensor, this.dependencies);
        break;
      default:
        break;
    }
    return specifics;
  }

  private buildSensorExtraInfo(sensor: Sensor): HTMLElement | null {
    switch (sensor.type) {
      case SensorType.NSVA:
        return NsvaNetworkSensorView.buildSensorExtraInfo(sensor, this.dependencies);
      default:
        break;
    }

    return null;
  }

  private buildSensorWarningMessageHtml(sensor: Sensor): string {
    let warningMessage = ``;
    switch (sensor.type) {
      case SensorType.NSVA:
        warningMessage = NsvaNetworkSensorView.getDetailPanelWarningMessage(this.dependencies);
        break;
      default:
        break;
    }
    return warningMessage;
  }

  private buildSensorStatusHtml(sensor: Sensor): string {
    let sensorStatus = sensor.status;
    if (sensor.type === SensorType.NSVA && sensor.networkSensorStatus != null) {
      sensorStatus = sensor.networkSensorStatus;
    }
    const bulletMap = sensorStatusesToBullets;
    const labelMap = this.translations.sensorStatusLabels;
    const bullet = typeof bulletMap[sensorStatus] !== 'undefined' ? bulletMap[sensorStatus] : unknownSensorStatusBullet,
      label =
        typeof labelMap[sensorStatus] !== 'undefined'
          ? labelMap[sensorStatus]
          : this.translations.unknownSensorStatusLabel;

    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${this.translations.sensorStatusLabel}">
              <span class="has-bullet ${bullet}">${label}</span>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  private buildSensorResponseStatusHtml(sensor: Sensor): string {
    const labelMap = this.translations.sensorResponseLabels;
    const bullet =
        typeof sensorResponseStatusToBullets[sensor.responseActionStatus] !== 'undefined' ? sensorResponseStatusToBullets[sensor.responseActionStatus] : unknownSensorStatusBullet,
      label =
        typeof labelMap[sensor.responseActionStatus] !== 'undefined'
        ? labelMap[sensor.responseActionStatus]
        : this.translations.unknownSensorStatusLabel;

    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${this.translations.responseStatusLabel}" hasColon="true">
              <span class="has-bullet ${bullet}">${label}</span>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  private createSensorReasonElement(responseActionErrorMessage: string): HTMLElement {
    const labeledContent = document.createElement(WEB_COMPONENT_GZ_LABELED_CONTENT);
    labeledContent.setAttribute('label', '');
    const contentWrapper = this.createExpandableContent(responseActionErrorMessage, 'integration-sensor-error');
    labeledContent.appendChild(contentWrapper);

    return labeledContent;
  }

  private buildSensorErrorHtml(error: Error | null, lastErrorStatus: number): string {
    if (lastErrorStatus && notShowedSensorConnectivityErrors.includes(lastErrorStatus)) {
      return '';
    }

    let errorDescription = '';
    if (error) {
      errorDescription = !error.error_uri && error.error_description ? error.error_description + ' ' : '';
    }

    let translatedDescription = '';
    if (lastErrorStatus) {
      translatedDescription = this.stringsTranslator.translateSensorConnectivityErrorCodes(lastErrorStatus);
    }

    let errorText = errorDescription + translatedDescription;

    if (error && error.error_uri && lastErrorStatus !== SensorConnectivityErrors.LICENSE_INACTIVE) {
      errorText += ` (<gz-link type="underline" href="${error.error_uri}"><span>${this.dependencies.translateFn(
        'View more'
      )}</span></gz-link>)`;
    }

    return `
      <${WEB_COMPONENT_GZ_LABELED_CONTENT} label="">
        <${WEB_COMPONENT_GZ_TEXT} class="integration-sensor-error expandable-text" fontSize="${FontSizes.PARAGRAPH_12}">
          ${errorText}
        </${WEB_COMPONENT_GZ_TEXT}>
      </${WEB_COMPONENT_GZ_LABELED_CONTENT}>
    `;
  }

  private buildSensorActionButton(sensor: Sensor): GzButtonWebComponent | null {
    const status = sensor.status;

    if (SensorStatus.OFFLINE === status) {
      return null;
    }

    if (SensorStatus.ACTION_NEEDED === status) {
      return null;
    }

    if (SensorStatus.NOT_CONFIGURED === status) {
      return this.buildSensorConfigureButton();
    }

    const enabledStatus = SensorStatus.ACTIVE === status ? SensorEnabledStatus.DISABLED : SensorEnabledStatus.ENABLED;
    return this.buildSensorSetEnabledStatusActionButton(sensor, enabledStatus);
  }

  private buildSensorConfigureButton(): GzButtonWebComponent {
    const button = new GzButtonWebComponent();
    button.className = 'mt-2 btn-toggle-enable-sensor';
    button.type = ButtonTypes.FULL;
    button.buttonHeight = ButtonHeights.SMALL;
    button.buttonFontSize = ButtonFontSizes.SMALL;
    button.innerText = this.dependencies.translateFn('Configure');
    if (!this.hasIntegrationFeature) {
      button.disabled = true;
      return button;
    }

    button.onclick = () => this.dom.$sideMenu.dispatchEvent(new CustomEvent(IntegrationDetailsPanelEvents.EDIT_ACTION));
    return button;
  }

  private buildSensorSetEnabledStatusActionButton(
    sensor: Sensor,
    enabledStatus: SensorEnabledStatus
  ): GzButtonWebComponent {
    const button = new GzButtonWebComponent();
    button.className = 'mt-2 btn-toggle-enable-sensor';
    button.type = ButtonTypes.OUTLINE;
    if (SensorEnabledStatus.DISABLED === enabledStatus) {
      button.color = ButtonColors.NEGATIVE;
    }
    button.buttonHeight = ButtonHeights.SMALL;
    button.buttonFontSize = ButtonFontSizes.SMALL;
    button.innerText =
      SensorEnabledStatus.DISABLED === enabledStatus
        ? this.translations.disableSensorActionButtonLabel
        : this.translations.enableSensorActionButtonLabel;

    if (enabledStatus === SensorEnabledStatus.ENABLED && !this.hasIntegrationFeature) {
      button.disabled = true;
      return button;
    }

    button.onclick = () =>
      this.dom.$sideMenu.dispatchEvent(
        new CustomEvent(IntegrationDetailsPanelEvents.SENSOR_SET_ENABLED_STATUS_ACTION, {
          detail: {
            sensor,
            enabledStatus,
          },
        })
      );

    return button;
  }

  private buildDeleteSensorButton(
    sensor: Sensor,
    isJustOneSensor: boolean
  ): GzButtonWebComponent | GzTooltipWebComponent | null {
    switch (sensor.type) {
      case SensorType.AD_ONPREM_DOMAIN:
        const enableDeleteButton =
            (sensor.status === SensorStatus.OFFLINE || sensor.status === SensorStatus.BEST_NOT_INSTALLED) &&
            !isJustOneSensor

        const button = this.buildButton(sensor, enableDeleteButton);
        if (enableDeleteButton) {
          return button;
        }

        const tooltip = new GzTooltipWebComponent();
        let tooltipText = '';
        if (sensor.status !== SensorStatus.OFFLINE && sensor.status !== SensorStatus.BEST_NOT_INSTALLED) {
          tooltipText = this.dependencies.translateFn('The sensor must be offline or unmanaged.');
        } else if (isJustOneSensor) {
          tooltipText = this.dependencies.translateFn(
            'Cannot delete the last domain controller sensor in the integration.'
          );
        }

        tooltip.tooltipText = tooltipText;
        tooltip.appendChild(button);
        return tooltip;
      default:
        return null;
    }
  }

  private buildButton(sensor: Sensor, enabledStatus: boolean): GzButtonWebComponent {
    const button = new GzButtonWebComponent();
    button.type = ButtonTypes.OUTLINE;
    button.className = 'mt-2 btn-toggle-enable-sensor';
    button.buttonHeight = ButtonHeights.SMALL;
    button.buttonFontSize = ButtonFontSizes.SMALL;
    button.innerText = this.dependencies.translateFn('Delete');

    if (!enabledStatus) {
      button.disabled = true;
      return button;
    }

    button.onclick = () => {
      this.dom.$sideMenu.dispatchEvent(
        new CustomEvent(IntegrationDetailsPanelEvents.BUTTON_CLICKED_DELETE_SENSOR, {
          detail: {
            sensor,
          },
        })
      );
    };

    return button;
  }

  private renderActions(
    status: IntegrationStatus,
    integrationType: IntegrationType,
    sensors: Sensor[],
    companyId: string,
    nonUserConfigurable: boolean
  ): void {
    let integrationStatus = status;
    if (sensors[0]?.type === SensorType.NSVA) {
      integrationStatus =
        sensors[0].networkSensorStatus != null
          ? this.getIntegrationStatusFromSensorStatus(sensors[0].networkSensorStatus)
          : status;
    }

    const addButton = this.buildAddLicenseButton(integrationStatus, companyId, nonUserConfigurable);
    const editButton = this.buildEditActionButton(integrationStatus, integrationType);
    const enabledStatusActionButton = this.buildSetEnabledStatusActionButton(integrationStatus, nonUserConfigurable);
    const technicalSupportFormButton = this.buildTechnicalSupportForm(integrationStatus, nonUserConfigurable);
    const deleteButton = this.buildDeleteActionButton(integrationStatus, integrationType, sensors, nonUserConfigurable);
    const buttonList = [addButton, enabledStatusActionButton, editButton, technicalSupportFormButton, deleteButton];
    for (const button of buttonList) {
      if (button) {
        this.dom.$actionsContainer.appendChild(button);
      }
    }
  }

  private buildSetEnabledStatusActionButton(
    status: IntegrationStatus,
    nonUserConfigurable: boolean
  ): GzButtonWebComponent | null {
    if (
      IntegrationStatus.ACTION_NEEDED === status ||
      nonUserConfigurable ||
      status === IntegrationStatus.NOT_LICENSED ||
      !this.hasIntegrationFeature
    ) {
      return null;
    }

    const button = new GzButtonWebComponent(),
      enabledStatus = IntegrationStatus.ACTIVE === status ? SensorEnabledStatus.DISABLED : SensorEnabledStatus.ENABLED;
    button.className = 'btn-toggle-enable-integration';
    button.type = SensorEnabledStatus.DISABLED === enabledStatus ? ButtonTypes.OUTLINE : ButtonTypes.FULL;
    button.innerText =
      SensorEnabledStatus.DISABLED === enabledStatus
        ? this.translations.disableActionButtonLabel
        : this.translations.enableActionButtonLabel;

    if (enabledStatus === SensorEnabledStatus.ENABLED && !this.hasIntegrationFeature) {
      button.disabled = true;
      return button;
    }

    button.onclick = () =>
      this.dom.$sideMenu.dispatchEvent(
        new CustomEvent(IntegrationDetailsPanelEvents.SET_ENABLED_STATUS_ACTION, { detail: { enabledStatus } })
      );

    return button;
  }

  private buildTechnicalSupportForm(
    status: IntegrationStatus,
    nonUserConfigurable: boolean
  ): GzButtonWebComponent | null {
    if (!nonUserConfigurable || status !== IntegrationStatus.ACTION_NEEDED) {
      return null;
    }
    const button = new GzButtonWebComponent();
    button.className = 'btn-open-technical-support-form';
    button.type = ButtonTypes.QUIET;
    button.color = ButtonColors.SECONDARY;
    button.innerText = this.translations.technicalSupportRequestButtonLabel;

    button.onclick = () =>
      this.dom.$sideMenu.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.OnShowTechnicalRequest, {
          bubbles: true,
          composed: true,
        })
      );

    return button;
  }

  private buildAddLicenseButton(
    status: IntegrationStatus,
    companyId: string,
    nonUserConfigurable: boolean
  ): GzTooltipWebComponent | null {
    let disableButton = false;
    if (status === IntegrationStatus.NOT_LICENSED || !this.hasIntegrationFeature) {
      disableButton = false;
    } else if (nonUserConfigurable && status !== IntegrationStatus.ACTION_NEEDED) {
      disableButton = true;
    } else {
      return null;
    }

    const gzToolTip = new GzTooltipWebComponent();
    const button = new GzButtonWebComponent();

    button.className = 'btn-add-license';
    button.type = ButtonTypes.FULL;
    button.color = ButtonColors.PRIMARY;

    button.onclick = () => {
      this.dom.$sideMenu.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.OnLicenseAdd, {
          detail: { companyId: companyId },
          bubbles: true,
          composed: true,
        })
      );
      this.dom.$sideMenu.closeSideMenu();
    }

    button.innerText = this.translations.addLicenseActionButtonLabel;

    if (disableButton) {
      button.disabled = disableButton;
    }

    if (!this.dependencies.Ext.userHasRight('companyManager') || !this.dependencies.Ext.canAddLicense(companyId)) {
      button.disabled = true;
      gzToolTip.tooltipText = this.dependencies.translateFn('Please contact your administrator');
    }

    button.innerText = this.translations.addLicenseActionButtonLabel;

    gzToolTip.appendChild(button);
    return gzToolTip;
  }

  private buildDeleteActionButton(
    status: IntegrationStatus,
    integrationType: IntegrationType,
    sensors: Sensor[],
    nonUserConfigurable: boolean
  ): GzButtonWebComponent {
    let shouldDisableDeleteIntegrationButton = false;
    if (
      !(
        integrationType !== IntegrationType.NSVA ||
        [SensorStatus.OFFLINE, SensorStatus.BEST_NOT_INSTALLED].includes(sensors[0].status)
      ) ||
      (nonUserConfigurable && IntegrationStatus.NOT_LICENSED !== status)
    ) {
      shouldDisableDeleteIntegrationButton = true;
    }

    if (
      integrationType === IntegrationType.NSVA &&
      ![SensorStatus.OFFLINE, SensorStatus.BEST_NOT_INSTALLED].includes(
        sensors[0].networkSensorStatus ?? sensors[0].status
      )
    ) {
      shouldDisableDeleteIntegrationButton = true;
    }

    const button = new GzButtonWebComponent();
    button.className = 'btn-delete-integration';
    button.type = ButtonTypes.QUIET;
    button.color = ButtonColors.NEGATIVE;
    button.innerText = this.translations.deleteActionButtonLabel;
    if (shouldDisableDeleteIntegrationButton) {
      button.disabled = true;
      return button;
    }
    button.onclick = () =>
      this.dom.$sideMenu.dispatchEvent(new CustomEvent(IntegrationDetailsPanelEvents.DELETE_ACTION));

    return button;
  }

  private buildEditActionButton(
    status: IntegrationStatus,
    integrationType: IntegrationType
  ): GzButtonWebComponent | null {
    if (
      INTEGRATION_WITHOUT_EDIT.includes(integrationType) ||
      status === IntegrationStatus.NOT_LICENSED ||
      !this.hasIntegrationFeature
    ) {
      return null;
    }
    const button = new GzButtonWebComponent();
    button.className = 'btn-edit-integration';
    button.type = ButtonTypes.QUIET;
    button.color = ButtonColors.SECONDARY;
    button.innerText = this.translations.editActionButtonLabel;

    if (!this.hasIntegrationFeature) {
      button.disabled = true;
      return button;
    }

    button.onclick = () => this.dom.$sideMenu.dispatchEvent(new CustomEvent(IntegrationDetailsPanelEvents.EDIT_ACTION));

    return button;
  }

  private buildSetDisabledStatusDialog(
    title: string,
    text: string,
    confirmCallback: EventListenerOrEventListenerObject
  ): void {
    const windowBodyHtml = `
      <div style="margin-top: -18px">
        <${WEB_COMPONENT_GZ_TEXT} class="mb-2" fontSize="${FontSizes.HEADING_6}">
          ${text}
        </${WEB_COMPONENT_GZ_TEXT}>
      </div>
    `;

    const windowFooterHtml = `
      <${WEB_COMPONENT_GZ_BUTTON}
        id="cancel"
        class="mr-2"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.SECONDARY}"
      >
        ${this.translations.setEnabledStatusDialogCancelButtonLabel}
      </${WEB_COMPONENT_GZ_BUTTON}>
      <${WEB_COMPONENT_GZ_BUTTON} id="confirm" type="${ButtonTypes.OUTLINE}">
        ${this.translations.disableDialogConfirmButtonLabel}
      </${WEB_COMPONENT_GZ_BUTTON}>
    `;

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, title, null, confirmCallback);
  }

  private getIntegrationStatusFromSensorStatus(sensorStatus: SensorStatus): IntegrationStatus {
    let integrationStatus = IntegrationStatus.ACTION_NEEDED;
    switch (sensorStatus) {
      case SensorStatus.NOT_LICENSED:
        integrationStatus = IntegrationStatus.NOT_LICENSED;
        break;
      case SensorStatus.DISABLED:
        integrationStatus = IntegrationStatus.DISABLED;
        break;
      case SensorStatus.ACTIVE:
        integrationStatus = IntegrationStatus.ACTIVE;
        break;
      default:
        break;
    }
    return integrationStatus;
  }

  private createExpandableContent(text: string, cssClass: string, maxLength:number = 64): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = cssClass;

    const textElement = document.createElement('div');
    textElement.className = 'expandable-text';

    const decoder = document.createElement('div');
    decoder.innerHTML = text;
    const decodedText = decoder.textContent || '';

    const isExpandable = decodedText.length > maxLength;
    const shortText = isExpandable ? decodedText.substring(0, maxLength) + '...' : decodedText;

    textElement.textContent = shortText;

    // Only show button if text is long enough
    if (isExpandable) {
      const button = new GzButtonWebComponent();
      button.type = ButtonTypes.QUIET;
      button.color = ButtonColors.PRIMARY;
      button.buttonHeight= ButtonHeights.SMALL;
      button.lowercase = true;
      button.className = 'expand-button';
      button.style.marginTop = '4px';
      button.innerText = this.dependencies.translateFn('Read more');

      let expanded = false;
      button.addEventListener('click', () => {
        expanded = !expanded;
        if (expanded) {
          textElement.textContent = decodedText;
          button.innerText = this.dependencies.translateFn('Read less');
        } else {
          textElement.textContent = shortText;
          button.innerText = this.dependencies.translateFn('Read more');
        }
      });

      wrapper.appendChild(textElement);
      wrapper.appendChild(button);
    } else {
      wrapper.appendChild(textElement);
    }

    return wrapper;
  }
}
